// this is aliased in webpack config based on server/client build
import {createAPI} from 'create-api';
import {entitiesMaxAge} from '../../config/cache';
import {
    getBaseDataGamePlatformsKey,
    getGameCategoryItemBySlugKey,
    getGamePlatformItemKey,
    getGamePlatformsKey,
    getGameVirtualCategoryItemByInterfaceCode,
    getHelpCategoriesKey,
    getLayoutPartsKey,
    getNewsCategoriesKey,
    getNewsItemKey,
    getNewsKey, getNewsTagsKey,
    getPromotionsDataKey,
    getSliderNewsKey,
    getSliderPromotionsKey,
    getWinnerListKey,
    getTranslationMessagesKey,
    getSliderSlidesKey, getVipLevelsKey,
} from "../util/cache/cachekeyRepository";

const api = createAPI();

export function fetchUser(data) {
    return fetchData('/user/authentication/fetch', data);
}

export function fetchUserNotifications(data) {
    return fetchData('user/notifications', data);
}

export function setUserNotificationsRead(data) {
    return fetchData('user/notifications/read', data);
}

export function getOAuthRedirect(data) {
    return fetchData('user/oauth/redirect', data);
}

export function loginUser(data) {
    return fetchData('user/authentication/login', data);
}

export function logoutUser(data) {
    return fetchData('user/authentication/logout', data);
}

export function getPhoneVerificationCaptcha(data = {}) {
    return fetchData('user/verify/phone/captcha', data);
}

export function sendPhoneVerificationCode(data) {
    return fetchData('user/verify/phone/send', data);
}

export function checkPhoneVerificationCode(data) {
    return fetchData('user/verify/phone/check', data);
}

export function checkRegisterAvailability(data) {
    return fetchData('user/registration/check/availability', data);
}

export function fetchRegistrationFormData(data) {
    return fetchData('user/registration/form/data', data);
}

export function fetchAllCountries(data) {
    return fetchData('country/all', data);
}

export function registerUser(data) {
    return fetchData('user/registration/register', data);
}

export function quickRegisterUser(data) {
    return fetchData('user/registration/quick-register', data);
}

export function completeRegisterUser(data) {
    return fetchData('user/registration/complete-register', data);
}

export function checkUserRegistrationData(data) {
    return fetchData('user/registration/check/data', data);
}

export function checkUserRegistrationPhone(data) {
    return fetchData('user/registration/check/phone', data);
}

export function sendEmailVerificationCode(data) {
    return fetchData('user/registration/verify/email/send', data);
}

export function checkConfirmUserSignup(data) {
    return fetchData('user/registration/confirm/signup', data);
}

export function fetchUserBalance(data) {
    return fetchData('user/account/balance', data);
}

export function fetchUserWithdrawableBalance(data) {
    return fetchData('user/account/balance/withdrawable', data);
}

export function fetchTranslationMessages(data) {
    return fetchData('translation/messages', data, getTranslationMessagesKey(data));
}

export function fetchPageData(url, data, cacheKey) {
    return fetchData(url, data, cacheKey);
}

export function fetchLayoutParts(data) {
    return fetchData('page/layout', data, getLayoutPartsKey(data));
}

export function fetchBaseDataGamePlatforms(data) {
    return fetchData('game/platform/base', data, getBaseDataGamePlatformsKey(data));
}

export function fetchGamePlatforms(data) {
    return fetchData('game/platform/items', data, getGamePlatformsKey(data));
}

export function fetchGamePlatformItem(data) {
    return fetchData('game/platform/item', data, getGamePlatformItemKey(data));
}

export function fetchGameCategoryItemBySlug(data) {
    return fetchData('game/category/slug', data, getGameCategoryItemBySlugKey(data));
}

export function fetchGameVirtualCategoryItem(data) {
    return fetchData('game/category/virtual', data, getGameVirtualCategoryItemByInterfaceCode(data));
}

export function fetchCategoryGames(data) {
    return fetchData('category/game/games', data);
}

export function fetchPlatformGames(data) {
    return fetchData('game/platform/games', data);
}

export function fetchNews(data) {
    return fetchData('news/page', data, getNewsKey(data));
}

export function fetchNewsItem(data) {
    return fetchData('news/item', data, getNewsItemKey(data));
}

export function fetchSliderNews(data) {
    return fetchData('news/slider', data, getSliderNewsKey(data));
}

export function fetchNewsTags(data) {
    return fetchData('news/tags', data, getNewsTagsKey(data));
}

export function fetchNewsCategories(data) {
    return fetchData('news/categories', data, getNewsCategoriesKey(data));
}

export function fetchSliderSlides(data) {
    return fetchData('slider/slides', data, getSliderSlidesKey(data));
}

export function fetchPromotionsData(data) {
    return fetchData('promotion/all', data, getPromotionsDataKey(data));
}

export function fetchSliderPromotions(data) {
    return fetchData('promotion/slider', data, getSliderPromotionsKey(data));
}

export function fetchSinglePromotion(data) {
    return fetchData('promotion/single', data);
}

export function fetchHelpCategories(data) {
    return fetchData('help/categories', data, getHelpCategoriesKey(data));
}

export function sendContactEmail(data) {
    return fetchData('help/contact/send', data);
}

export function fetchWinnerList(data) {
    return fetchData('game/winners', data, getWinnerListKey(), '', entitiesMaxAge.winnerList);
}

export function fetchSearchGames(data) {
    return fetchData('game/search', data);
}

export function fetchStartSearchGames(data) {
    return fetchData('game/search/start', data);
}

export function storeAddGameToFavorite(data) {
    return fetchData('game/favorite/add', data);
}

export function storeRemoveGameFromFavorite(data) {
    return fetchData('game/favorite/remove', data);
}

export function fetchUserDepositMethodsData(data) {
    return fetchData('payment/deposit/methods', data);
}

export function fetchUserDepositMethodsAlt(data) {
    return fetchData('payment/deposit/methods/alternative', data);
}

export function fetchUserWithdrawMethods(data) {
    return fetchData('payment/withdraw/methods', data);
}

export function hasPendingWithdrawals(data) {
    return fetchData('payment/withdraw/pending', data);
}

export function cancelAllWithdrawals(data) {
    return fetchData('payment/withdraw/cancel/all', data);
}

export function cancelWithdrawal(data) {
    return fetchData('payment/withdraw/cancel', data);
}

export function getDepositProcessFormData(data) {
    return fetchData('payment/process/form/data/deposit', data);
}

export function getWithdrawProcessFormData(data) {
    return fetchData('payment/process/form/data/withdraw', data);
}

export function validateDepositPaymentData(data) {
    return fetchData('payment/process/validate/deposit', data);
}

export function validateWithdrawPaymentData(data) {
    return fetchData('payment/process/validate/withdraw', data);
}

export function makeDepositProcess(data) {
    return fetchData('payment/process/deposit', data);
}

export function makeWithdrawProcess(data) {
    return fetchData('payment/process/withdraw', data);
}

export function checkTransactionStatus(data) {
    return fetchData('payment/transaction/status', data);
}

export function updateUserPassword(data) {
    return fetchData('user/password/update', data);
}

export function fetchQuestions(data) {
    return fetchData('/user/question/get', data);
}

export function getResetPasswordData(data) {
    return fetchData('/user/password/reset/data', data);
}

export function makeResetPasswordQuery(data) {
    return fetchData('user/password/reset/query', data);
}

export function resetUserPassword(data) {
    return fetchData('user/password/reset/update', data);
}

export function updateUserLanguage(data) {
    return fetchData('user/language/update', data);
}

export function updateSecurityQuestion(data) {
    return fetchData('user/question/update', data);
}

export function fetchTransactionsHistory(data) {
    return fetchData('user/history/transactions', data);
}

export function fetchBankingHistory(data) {
    return fetchData('user/history/banking', data);
}

export function fetchUserBonuses(data) {
    return fetchData('user/account/bonuses', data);
}

export function fetchPaymentBonuses(data) {
    return fetchData('user/account/payment-bonuses', data);
}

export function checkBonusCode(data) {
    return fetchData('user/account/bonus/check', data);
}

export function redeemBonusCode(data) {
    return fetchData('user/account/bonus/redeem', data);
}

export function cancelUserBonus(data) {
    return fetchData('user/account/bonus/cancel', data);
}

export function fetchUserLoyaltyPoints(data) {
    return fetchData('user/account/loyalty', data);
}

export function cashInLoyaltyPoints(data) {
    return fetchData('user/account/loyalty/cash', data);
}

export function getUserDocuments(data) {
    return fetchData('user/documents/get', data);
}

export function getUserDocumentsLevels(data) {
    return fetchData('user/documents/levels', data);
}

export function uploadUserDocument(data) {
    return fetchData('user/documents/upload', data);
}

export function fetchPersonalDepositLimits(data) {
    return fetchData('user/limits/personal', data);
}

export function resetPersonalDepositLimits(data) {
    return fetchData('user/limits/personal/reset', data);
}

export function setPersonalDepositLimits(data) {
    return fetchData('user/limits/personal/set', data);
}

export function fetchCysendCountries(data) {
    return fetchData('payment/cysend/countries', data);
}

export function fetchCysendCategories(data) {
    return fetchData('payment/cysend/categories', data);
}

export function fetchCysendProducts(data) {
    return fetchData('payment/cysend/products', data);
}

export function fetchCysendProductFaceValues(data) {
    return fetchData('payment/cysend/face-values', data);
}

export function sendRecommendedGameLaunchAction(data) {
    return fetchData('game/recommended/action/launch', data);
}

export function storeAddRecommendedGameToFavoriteAction(data) {
    return fetchData('game/recommended/action/favorite/add', data);
}

export function storeRemoveRecommendedGameFromFavoriteAction(data) {
    return fetchData('game/recommended/action/favorite/remove', data);
}

export function fetchLobbyRecommendedGames(data) {
    return fetchData('page/game/related', data);
}

export function fetchLotteryCurrentData(data) {
    return fetchData('lottery/current', data);
}

export function fetchLotteryFutureData(data) {
    return fetchData('lottery/future', data);
}

export function fetchLotteryPastData(data) {
    return fetchData('lottery/past', data);
}

export function fetchLotteryTicketsData(data) {
    return fetchData('lottery/tickets', data);
}

export function fetchLotteryWinnersData(data) {
    return fetchData('lottery/winners', data);
}

export function fetchFortuneWheels(data) {
    let url = data.sessionKey ? 'wheel/user/wheels' : 'wheel/guest/wheels';
    return fetchData(url, data);
}

export function getFortuneWheelData(data = {}) {
    return fetchData('wheel/data', data);
}

export function makeFortuneWheelSpin(data) {
    let url = data.sessionKey ? 'wheel/user/spin' : 'wheel/guest/spin';
    return fetchData(url, data);
}

export function getFortuneWheelCaptcha(data = {}) {
    return fetchData('wheel/guest/captcha', data);
}

export function fetchTournament(data) {
    return fetchData('tournament/item', data);
}

export function fetchTournaments(data) {
    return fetchData('tournament/list', data);
}

export function subscribeGuestToNewsLetters(data) {
    return fetchData('guest/newsletter/subscribe', data);
}

export function fetchPagePartContentForDownload(data) {
    return fetchData('page/part/content/download', data);
}

export function fetchPagePartContentForPrint(data) {
    return fetchData('page/part/content/print', data);
}

export function lockPlayer(data) {
    return fetchData('user/account/lock', data);
}

export function switchUserTwoFactorAuthEnabling(data) {
    return fetchData('user/account/two-factor-auth/switch', data);
}

export function fetchVipLevels(data) {
    return fetchData('user/vip-levels/info', data, getVipLevelsKey());
}

function fetchData(url, data, cacheKey = null, responseDataPropertyName = null, cacheTime = null) {
    let cachedData = cacheKey && getCachedData(cacheKey);

    if (cachedData) {
        return Promise.resolve(cachedData);
    } else {
        return new Promise((resolve, reject) => {
            api.axios.post(url, data)
                .then(response => {
                    let responseData;

                    if (responseDataPropertyName) {
                        responseData = response.data[responseDataPropertyName]
                    } else {
                        responseData = response.data;
                    }

                    cacheKey && setCachedData(cacheKey, cacheTime, responseData);

                    resolve(responseData);
                })
                .catch(error => {
                    reject(error)
                });
        });
    }
}

function getCachedData(cacheKey) {
    return api.onServer && api.cache && api.cache.get(cacheKey);
}

function setCachedData(cacheKey, cacheTime, data) {
    return api.onServer && api.cache && api.cache.set(cacheKey, data, {
        ttl: cacheTime,
    });
}